import { useEffect, useMemo, useState } from "react";
import { FaPlus } from "react-icons/fa6";
import { toast } from "react-toastify";
import Select from "react-select";
import isGHMC from "../../../Common/helper/helpar";
import { GetCircle, GetZone } from "../../../Apis/organizationApi";
import complaintImage from "../../../static_images/swm5-1.jpeg";
import AddComplaintForm from "./AddComplaintForm";
import EditComplaintForm from "./EditComplaintForm";
import ComplaintExpandableTable from "./ComplaintExpandableTable";
import { ExportComplaint } from "./ExportComplaint";
import { complaintOptions, useComplaintStore } from "./ComplaintStore/useComplaintStore";
import { SELECT_MENU_PORTAL_STYLES } from "./complaintFormShared";

const statusBadgeStyles = {
  Open: { color: "#0d6efd", backgroundColor: "#cfe2ff" },
  "In Progress": { color: "#9a6700", backgroundColor: "#fff3cd" },
  Resolved: { color: "#0f5132", backgroundColor: "#d1e7dd" },
  Closed: { color: "#495057", backgroundColor: "#e9ecef" },
  Invalid: { color: "#842029", backgroundColor: "#f8d7da" },
};

const toList = (response) =>
  Array.isArray(response) ? response : response?.data ?? [];

const mapCircleToWardOption = (circle) => ({
  ...circle,
  wardId: circle.circleId?.toString(),
  circleId: circle.circleId?.toString(),
  wardName: circle.circleName ?? "",
  zoneId: circle.zoneId?.toString() ?? "",
});

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const ComplaintRootComponent = () => {
  const isProd = isGHMC();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [zones, setZones] = useState(complaintOptions.fallbackZones);
  const [wards, setWards] = useState([]);
  const {
    filteredComplaints,
    filters,
    isLoading,
    pagination,
    updateFilter,
    applyFilters,
    resetFilters,
    fetchComplaints,
    fetchGrievanceCategories,
    fetchSlaCategories,
    grievanceCategories,
    grievanceCategoriesBySLA,
    fetchGrievanceCategoriesBySLA,
    slaCategories,
    statusOptions,
    fetchStatusOptions,
  } = useComplaintStore();

  useEffect(() => {
    fetchComplaints();
    fetchGrievanceCategories();
    fetchSlaCategories();
    fetchStatusOptions();
    if (filters.complaintTypeId) {
      fetchGrievanceCategoriesBySLA(filters.complaintTypeId);
    }
  }, [fetchComplaints, fetchGrievanceCategories, fetchSlaCategories, fetchStatusOptions, fetchGrievanceCategoriesBySLA, filters.complaintTypeId]);

  const loadZones = async () => {
    try {
      const response = await GetZone();
      const delhiZones = (Array.isArray(response) ? response : [])
        .filter(
          (zone) =>
            zone.isActive !== false &&
            String(zone.zoneName || "").toLowerCase().includes("delhi")
        )
        .map((zone) => ({
          ...zone,
          zoneId: zone.zoneId?.toString(),
        }));
      if (delhiZones.length) {
        setZones(delhiZones);
        const preferred =
          delhiZones.find((zone) =>
            /delhi central/i.test(String(zone.zoneName))
          ) || delhiZones[0];
        if (preferred?.zoneId && !filters.zoneId) {
          updateFilter("zoneId", String(preferred.zoneId));
        }
      }
    } catch {
      setZones(complaintOptions.fallbackZones);
    }
  };

  const loadAllWards = async () => {
    try {
      const response = await GetCircle();
      setWards(
        toList(response)
          .filter((circle) => circle.isActive !== false)
          .map(mapCircleToWardOption)
      );
    } catch {
      setWards([]);
    }
  };

  useEffect(() => {
    loadZones();
    loadAllWards();
  }, []);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    if (name === "fromDate" && filters.toDate && value > filters.toDate) {
      updateFilter("fromDate", value);
      updateFilter("toDate", value);
      return;
    }

    if (name === "toDate" && filters.fromDate && value < filters.fromDate) {
      toast.error("To Date cannot be before From Date.");
      return;
    }

    updateFilter(name, value);
  };

  const handleApplyFilters = async () => {
    const { fromDate, toDate } = filters;

    if (fromDate && toDate && fromDate > toDate) {
      toast.error("To Date cannot be before From Date.");
      return;
    }

    await applyFilters();
  };

  const handleAdd = () => {
    setEditRow(null);
    setShowAddModal(true);
  };

  const handleEdit = (row) => {
    setEditRow(row);
    setShowEditModal(true);
  };

  const handleResetFilters = async () => {
    await resetFilters();
  };

  const handlePageChange = (page) => {
    fetchComplaints({ currentPage: page });
  };

  const handlePageSizeChange = (pageSize) => {
    fetchComplaints({ currentPage: 1, pageSize });
  };

  const wardFilterOptions = useMemo(
    () =>
      wards.map((ward) => ({
        value: ward.wardId,
        label: ward.wardName || ward.circleName,
      })),
    [wards]
  );

  const complaintTypeFilterOptions = useMemo(
    () =>
      slaCategories.map((type) => ({
        value: String(type.slaCategoryId),
        label: type.categoryName,
      })),
    [slaCategories]
  );

  const complaintCategoryFilterOptions = useMemo(() => {
    const categories = filters.complaintTypeId ? grievanceCategoriesBySLA : grievanceCategories;
    return categories.map((category) => ({
      value: String(category.grievanceCategoryId),
      label: category.categoryName,
    }));
  }, [grievanceCategories, grievanceCategoriesBySLA, filters.complaintTypeId]);

  const statusFilterOptions = statusOptions.length
    ? statusOptions
    : complaintOptions.complaintFilterStatusOptions;

  return (
    <>
      <div className="content text-start">
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mt-3">
            <div>
              <h5 className="mb-1 fw-semibold">All Citizen Complaint Information</h5>
              <p className="text-muted mb-0">
                Manage and monitor citizen complaints, grievances, SLA tracking and
                resolution activities.
              </p>
            </div>
            <div className="d-flex align-items-center flex-wrap">
              <button type="button" className="button-container" onClick={handleAdd}>
                <FaPlus className="font-size-20 me-1" /> Add Complaint
              </button>
              <ExportComplaint complaintData={filteredComplaints} />
            </div>
          </div>

          <div className="card shadow mt-3">
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Zone</label>
                  <select
                    name="zoneId"
                    value={filters.zoneId}
                    onChange={handleFilterChange}
                    className="form-select bg-white"
                  >
                    {zones.map((zone) => (
                      <option key={zone.zoneId} value={zone.zoneId}>
                        {zone.zoneName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Ward</label>
                  <Select
                    inputId="wardId"
                    name="wardId"
                    classNamePrefix="react-select"
                    options={wardFilterOptions}
                    value={
                      wardFilterOptions.find(
                        (option) => option.value === filters.wardId
                      ) || null
                    }
                    onChange={(selected) =>
                      updateFilter("wardId", selected ? selected.value : "")
                    }
                    placeholder="Select Wards"
                    isClearable
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={SELECT_MENU_PORTAL_STYLES}
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Complaint Type</label>
                  <Select
                    inputId="complaintTypeId"
                    name="complaintTypeId"
                    classNamePrefix="react-select"
                    options={complaintTypeFilterOptions}
                    value={
                      complaintTypeFilterOptions.find(
                        (option) => option.value === String(filters.complaintTypeId)
                      ) || null
                    }
                    onChange={(selected) => {
                      const val = selected ? selected.value : "";
                      updateFilter("complaintTypeId", val);
                      updateFilter("categoryId", "");
                      if (val) {
                        fetchGrievanceCategoriesBySLA(val);
                      }
                    }}
                    placeholder="Select Type"
                    isClearable
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={SELECT_MENU_PORTAL_STYLES}
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Complaint Category</label>
                  <Select
                    inputId="categoryId"
                    name="categoryId"
                    classNamePrefix="react-select"
                    options={complaintCategoryFilterOptions}
                    value={
                      complaintCategoryFilterOptions.find(
                        (option) => option.value === String(filters.categoryId)
                      ) || null
                    }
                    onChange={(selected) =>
                      updateFilter("categoryId", selected ? selected.value : "")
                    }
                    placeholder="Select Category"
                    isClearable
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={SELECT_MENU_PORTAL_STYLES}
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    max={filters.toDate || undefined}
                    onChange={handleFilterChange}
                    className="form-control bg-white"
                  />
                </div>
                {/* <div className="col-md-3 mb-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    min={filters.fromDate || undefined}
                    max={new Date().toLocaleDateString("en-CA")}

                    onChange={handleFilterChange}
                    className="form-control bg-white"
                  /> */}
                <div className="col-md-3 mb-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    min={filters.fromDate || undefined}
                    max={new Date().toLocaleDateString("en-CA")}
                    onChange={handleFilterChange}
                    className="form-control bg-white"
                  />


                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    className="form-select bg-white"
                  >
                    <option value="">All Status</option>
                    {statusFilterOptions.map((status) => (
                      <option key={status.label} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-12 mb-3 d-flex gap-2">
                  <button
                    type="button"
                    style={{
                      backgroundColor: "#003366",
                      color: "#FFFFFF",
                      border: "none",
                      fontWeight: 600,
                      padding: "8px 24px",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                    onClick={handleApplyFilters}
                  >
                    APPLY FILTERS
                  </button>
                  <button
                    type="button"
                    style={{
                      backgroundColor: "#FDE8E8",
                      color: "#8B1A1A",
                      border: "none",
                      fontWeight: 600,
                      padding: "8px 24px",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                    onClick={handleResetFilters}
                  >
                    RESET
                  </button>
                </div>
              </div>

              <ComplaintExpandableTable
                loading={isLoading}
                rows={filteredComplaints}
                complaintImage={complaintImage}
                statusBadgeStyles={statusBadgeStyles}
                isProd={isProd}
                onEdit={handleEdit}
                pagination={pagination}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        </div>
      </div>

      <AddComplaintForm
        showModal={showAddModal}
        setShowModal={setShowAddModal}
        zones={zones}
        wards={wards}
      />

      <EditComplaintForm
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        editRow={editRow}
        wards={wards}
      />
    </>
  );
};

export default ComplaintRootComponent;
